import i18n from 'i18next';
import { LANG_en_gb, LANG_en_us } from './langs';

const defaultIntlOpts = { minimumFractionDigits: 0, maximumFractionDigits: 2 };
const defaultWholeIntlOpts = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

const getNumberLocale = () => {
    const activeLanguage = i18n.resolvedLanguage ?? i18n.language;

    if (activeLanguage === LANG_en_us) return 'en-US';
    if (activeLanguage === LANG_en_gb) return 'en-GB';

    if (typeof navigator !== 'undefined' && navigator.language) {
        return navigator.language;
    }

    return 'en-GB';
};

export const formatNumber = (
    value: number | string,
    intlOptions: Intl.NumberFormatOptions = defaultIntlOpts,
) => Number(value).toLocaleString(getNumberLocale(), intlOptions);

export const formatNumberWhole = (
    value: number | string,
    intlOptions: Intl.NumberFormatOptions = defaultWholeIntlOpts,
) => Number(value).toLocaleString(getNumberLocale(), intlOptions);

export const formatNumberCompact = (
    value: number | string,
    minCompactNumber: number,
    intlOptions?: Intl.NumberFormatOptions,
) => {
    const numberVal = Number(value);

    if (numberVal < minCompactNumber) {
        return formatNumber(numberVal, intlOptions);
    }

    return numberVal.toLocaleString(getNumberLocale(), { notation: 'compact' });
};

export const formatCompactLower = (value: number): string => {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    const fmt = (v: number, s: 'k' | 'm' | 'b') => {
        const str = (v < 10 ? v.toFixed(1) : Math.round(v).toString()).replace(/\.0$/, '');
        return `${sign}${str}${s}`;
    };

    if (abs > 0 && abs < 0.5) {
        return value.toLocaleString(getNumberLocale(), {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    // Pick the unit after rounding, so 999,600 is "1m" rather than "1000k".
    const units: [number, 'k' | 'm' | 'b'][] = [
        [1e3, 'k'],
        [1e6, 'm'],
        [1e9, 'b'],
    ];
    const rounded = (v: number) => (v < 10 ? Math.round(v * 10) / 10 : Math.round(v));
    for (let i = units.length - 1; i >= 0; i -= 1) {
        const [divisor, suffix] = units[i];
        if (abs >= divisor) {
            const bigger = units[i + 1];
            if (bigger && rounded(abs / divisor) >= 1000) return fmt(abs / bigger[0], bigger[1]);
            return fmt(abs / divisor, suffix);
        }
    }
    return value.toLocaleString(getNumberLocale());
};
